/**
 * Change one's own password on Profil (owner 2026-10-07; POST /account/password). The current password
 * is asked for when the account has one; a demo opened by link or by Google has none and sets its first.
 * Every other device is signed out; this one carries on with the new token the server answers.
 */
import { ApiError, errorText, get, post } from '@/api/client';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { useAvailability } from '@/pages/konto/shared';
import { Button, toast } from '@/ui';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Card } from './parts';

export function PassordCard() {
  // GET /account/me is only served where self-service is open (404 'not_enabled' otherwise, #272 B-T06).
  // Without it no account can be passwordless (a demo by link or by Google is self-service), so the
  // current password is asked for, as for every account that signed in with one
  const { signup } = useAvailability();
  const me = useQuery({
    queryKey: ['account-me'],
    queryFn: () => get<{ has_password: boolean }>('/account/me'),
    enabled: signup === true,
  });
  const supporting = useAuth((s) => !!s.user?.support);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [err, setErr] = useState<{ field: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // the field a refusal is about is marked and focused, as on Send søknad (#272 B-R11)
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (err) form.current?.querySelector<HTMLElement>('[aria-invalid=true]')?.focus();
  }, [err]);
  const has = me.data?.has_password !== false;
  if (supporting) return null; // support only reads; this would be refused anyway

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    // the button is never silently disabled: each gap is said under its field (#272 B-39)
    if (has && !current)
      return setErr({
        field: 'current',
        text: T('Skriv det nåværende passordet.'),
      });
    if (next.length < 8)
      return setErr({
        field: 'password',
        text: T('Passordet må ha minst 8 tegn.'),
      });
    if (next !== again) return setErr({ field: 'again', text: T('Passordene er ikke like.') });
    setBusy(true);
    try {
      const r = await post<{ token: string | null; role: string | null }>('/account/password', {
        ...(has ? { current } : {}),
        password: next,
      });
      const { user } = useAuth.getState();
      if (r.token && user) useAuth.getState().setSession(r.token, { ...user, role: r.role ?? user.role });
      setCurrent('');
      setNext('');
      setAgain('');
      me.refetch();
      toast(T('Passordet er byttet. Du er logget ut på andre enheter.'));
    } catch (ex) {
      // the server's stable code names the field; screens never branch on the message
      const code = ex instanceof ApiError ? ex.code : null;
      const field =
        code === 'wrong_password'
          ? 'current'
          : code === 'password_too_short' || code === 'same_password'
            ? 'password'
            : '';
      setErr({ field, text: ex instanceof ApiError ? ex.message : errorText(ex) });
    } finally {
      setBusy(false);
    }
  }

  const about = (f: string) =>
    !!err && (err.field === f || (f === 'password' && !['current', 'again'].includes(err.field)));
  const marks = (f: string) => (about(f) ? { 'aria-invalid': true, 'aria-describedby': `pw-err-${f}` } : {});
  const shown = (f: string) =>
    about(f) && err ? (
      <span role="alert" id={`pw-err-${f}`} style={{ color: 'var(--dgr-i)' }}>
        {err.text}
      </span>
    ) : null;

  return (
    <Card title={has ? T('Bytt passord') : T('Lag et passord')} testid="password">
      <form ref={form} onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
        {has && (
          <label className="fld">
            <span className="lbl">{T('Nåværende passord')}</span>
            <input
              className="inp w"
              type="password"
              autoComplete="current-password"
              {...marks('current')}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
            {shown('current')}
          </label>
        )}
        <label className="fld">
          <span className="lbl">{T('Nytt passord, minst 8 tegn')}</span>
          <input
            className="inp w"
            type="password"
            autoComplete="new-password"
            {...marks('password')}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          {shown('password')}
        </label>
        <label className="fld">
          <span className="lbl">{T('Nytt passord en gang til')}</span>
          <input
            className="inp w"
            type="password"
            autoComplete="new-password"
            {...marks('again')}
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
          {shown('again')}
        </label>
        <div>
          <Button tone="pri" type="submit" disabled={busy}>
            {has ? T('Bytt passord') : T('Lagre passordet')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
